// src/components/inspectionBot/RecordTypeSelectorContent.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const RecordTypeSelectorContent = ({ isDarkMode, onNext, onBack, currentStep }) => {
  const navigate = useNavigate();
  
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedSubtype, setSelectedSubtype] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Load saved data and fetch record types on component mount
  useEffect(() => {
    // Load saved selections
    setSelectedGroup(localStorage.getItem('group') || '');
    setSelectedType(localStorage.getItem('type') || '');
    setSelectedSubtype(localStorage.getItem('subtype') || '');
    setSelectedCategory(localStorage.getItem('category') || '');
    setSelectedTemplate(localStorage.getItem('selectedTemplate') || '');
    
    fetchRecordTypes();
  }, []);

  const fetchRecordTypes = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/get-alt-ids', {
        headers: { Authorization: token }
      });

      if (response.data.success) {
        const recordDataLists = response.data.result || response.data.RecordDataLists;
        
        if (!recordDataLists) {
          throw new Error('RecordDataLists not found in response');
        }

        const { groups, types, subtypes, categories } = recordDataLists;

        // Parse the data (handle both string and array formats)
        const parseDataList = (data) => {
          if (Array.isArray(data)) return data;
          if (typeof data === 'string') {
            return data.split('\n')
              .map(item => item.replace(/^- /, '').trim())
              .filter(Boolean);
          }
          return [];
        };

        setGroups(parseDataList(groups));
        setTypes(parseDataList(types));
        setSubtypes(parseDataList(subtypes));
        setCategories(parseDataList(categories));

      } else {
        setErrorMessage(response.data.message || 'Failed to fetch record types');
      }
    } catch (err) {
      console.error('Error fetching record types:', err);
      
      if (err.response?.status === 401) {
        setErrorMessage('Your session has expired. Please sign in again.');
        localStorage.removeItem('token');
        navigate('/signin');
      } else {
        setErrorMessage('Failed to fetch record types. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isFormComplete = () => {
    return selectedGroup && selectedType && selectedSubtype && selectedCategory;
  };

  const handleNextClick = async () => {
    if (!isFormComplete()) {
      setErrorMessage('Please select all record type fields before proceeding.');
      return;
    }

    setErrorMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const recordTypeData = {
        group: selectedGroup,
        type: selectedType,
        subtype: selectedSubtype,
        category: selectedCategory,
        selectedTemplate: selectedTemplate
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/set-record-type`,
        recordTypeData,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Save to localStorage as backup
        localStorage.setItem('group', selectedGroup);
        localStorage.setItem('type', selectedType);
        localStorage.setItem('subtype', selectedSubtype);
        localStorage.setItem('category', selectedCategory);
        
        const recordTypeString = `${selectedGroup}/${selectedType}/${selectedSubtype}/${selectedCategory}`;
        localStorage.setItem('recordTypeString', recordTypeString);

        if (onNext) {
          onNext();
        }
      } else {
        setErrorMessage(response.data.message || 'Failed to save record type data');
      }
    } catch (err) {
      console.error('Error saving record type data:', err);
      
      if (err.response?.status === 401) {
        setErrorMessage('Your session has expired. Please sign in again.');
        localStorage.removeItem('token');
        navigate('/signin');
      } else {
        setErrorMessage('Failed to save record type data. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    switch(field) {
      case 'group':
        setSelectedGroup(value);
        break;
      case 'type':
        setSelectedType(value);
        break;
      case 'subtype':
        setSelectedSubtype(value);
        break;
      case 'category':
        setSelectedCategory(value);
        break;
    }
    setErrorMessage(''); // Clear error when user makes changes
  };

  return (
    <div className="grid grid-cols-[40%_60%] gap-8 h-full py-4">
      
      {/* Record Type Selection Section */}
      <div className={`rounded-xl border shadow-lg overflow-hidden flex flex-col transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#DBDCED]'
      }`}>
        <div className={`px-5 py-5 border-b transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-[#F5F5FA] border-[#DBDCED]'
        }`}>
          <h2 className={`text-lg font-semibold mb-1.5 ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
            To which record type will this communication apply?
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#41425A]'}`}>
            Select the record type that will trigger this communication
          </p>
        </div>
        
        <div className="flex-1 p-5 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Loading record types...
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Record Type Fields */}
              {[
                { label: 'Group', value: selectedGroup, options: groups, field: 'group' },
                { label: 'Type', value: selectedType, options: types, field: 'type' },
                { label: 'SubType', value: selectedSubtype, options: subtypes, field: 'subtype' },
                { label: 'Category', value: selectedCategory, options: categories, field: 'category' }
              ].map((item, index) => (
                <div key={index}>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-[#DBDCED]' : 'text-[#41425A]'
                  }`}>
                    {item.label} *
                  </label>
                  <div className="relative">
                    <select
                      value={item.value}
                      onChange={(e) => handleFieldChange(item.field, e.target.value)}
                      className={`w-full p-3 text-base border-2 rounded-xl transition-all duration-300 appearance-none
                        focus:ring-2 focus:ring-[#41425A]/50 focus:border-[#41425A] outline-none ${
                        isDarkMode
                          ? 'bg-gray-700/50 border-gray-600/50 text-white hover:bg-gray-700/70'
                          : 'bg-white/50 border-gray-300/50 text-gray-900 hover:bg-white/70'
                      } ${item.value ? 'border-green-500/30' : ''}`}
                      disabled={isLoading}
                    >
                      <option value="">Select {item.label}</option>
                      {item.options.map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}

              {/* Permit Number Search */}
              <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-gray-600' : 'border-[#DBDCED]'}`}>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-[#DBDCED]' : 'text-[#41425A]'
                }`}>
                  Or search by permit number
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="RESBLD2024-00087"
                    className={`flex-1 p-3 text-base border-2 rounded-xl transition-all duration-300
                      focus:ring-2 focus:ring-[#41425A]/50 focus:border-[#41425A] outline-none ${
                      isDarkMode
                        ? 'bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-400 hover:bg-gray-700/70'
                        : 'bg-white/50 border-gray-300/50 text-gray-900 placeholder-gray-500 hover:bg-white/70'
                    }`}
                  />
                  <button className={`px-4 py-3 rounded-xl transition-colors duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </div>

              {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  <div className="flex items-start">
                    <span className="mr-2">⚠️</span>
                    <div>
                      {errorMessage}
                      {errorMessage.includes('Failed to fetch') && (
                        <button
                          onClick={fetchRecordTypes}
                          className="block mt-1 text-xs text-red-700 hover:underline"
                        >
                          Try again
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview/Summary Section */}
      <div className={`rounded-xl border shadow-lg overflow-hidden flex flex-col transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#DBDCED]'
      }`}>
        <div className={`px-5 py-5 border-b transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-[#F5F5FA] border-[#DBDCED]'
        }`}>
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
            Record Type Summary
          </h2>
        </div>
        
        <div className="flex-1 p-5 overflow-hidden">
          {isFormComplete() ? (
            <div className="h-full flex flex-col">
              <div className={`mb-4 pb-3 border-b flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-[#DBDCED]'}`}>
                <div className={`text-base font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
                  Selected Record Type
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#41425A]'}`}>
                  This communication will apply to records matching this type
                </div>
              </div>
              
              {/* Record Type Details */}
              <div className={`flex-1 border rounded-lg p-4 overflow-y-auto transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-[#F5F5FA] border-[#DBDCED]'
              }`}>
                <div className="space-y-4">
                  {[
                    { label: 'Group', value: selectedGroup, icon: '🏢' },
                    { label: 'Type', value: selectedType, icon: '📋' },
                    { label: 'SubType', value: selectedSubtype, icon: '📝' },
                    { label: 'Category', value: selectedCategory, icon: '🏷️' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-lg">{item.icon}</span>
                      <div className="flex-1">
                        <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {item.label}
                        </div>
                        <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
                          {item.value}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {selectedTemplate && (
                    <div className={`mt-6 pt-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-lg">📄</span>
                        <div className="flex-1">
                          <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Communication Template
                          </div>
                          <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
                            {selectedTemplate}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={`mt-6 p-3 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                      ✅ Record type configuration complete
                    </div>
                    <div className={`text-xs mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      Ready to proceed to trigger configuration
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`h-full flex items-center justify-center text-center border-2 border-dashed rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'border-gray-600 text-gray-400' : 'border-[#DBDCED] text-[#41425A]'
            }`}>
              <div>
                <div className="text-4xl mb-4">📋</div>
                <div className="font-medium mb-2">Select record type fields to see summary</div>
                <div className="text-sm opacity-70">Choose group, type, subtype, and category</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordTypeSelectorContent;